import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MapPin, BadgeCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { fetchPerfilPublico } from "../../lib/profile";
import { Skeleton } from "../../design-system";
import { humanizeAxiosError } from "../../lib/api";

/**
 * PerfilPublicoPreviewPage — permite al usuario ver EXACTAMENTE cómo lo verán los demás.
 * Consume el endpoint público /api/perfil/global/{user_id} (proyección sin PII).
 * Alineado con Etapa 3 §4.6.
 */
export default function PerfilPublicoPreviewPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!user?.id) return;
      setLoading(true);
      try {
        const d = await fetchPerfilPublico(user.id);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(humanizeAxiosError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="perfil-publico-preview">
      {/* Banner de "vista previa" */}
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between p-4 rounded-2xl bg-brand-blue/5 border border-brand-blue/20 mb-6"
        data-testid="perfil-publico-banner"
      >
        <div className="flex items-start gap-2 text-sm text-brand-navy">
          <BadgeCheck size={18} className="mt-0.5 flex-shrink-0" />
          <p>
            <strong>Vista previa:</strong> así te ven otros usuarios en ConstruLink. Los datos privados (correo, teléfono) NO se muestran aquí.
          </p>
        </div>
        <Link
          to="/mi-perfil/global"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-brand-blue/30 text-brand-blue text-sm font-semibold hover:bg-brand-blue/5"
          data-testid="perfil-publico-volver"
        >
          <ArrowLeft size={14} /> Volver a editar
        </Link>
      </div>

      {loading && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="perfil-publico-loading">
          <div className="flex gap-4">
            <Skeleton variant="avatar" />
            <div className="flex-1"><Skeleton variant="title" /><Skeleton variant="text" className="mt-2" /></div>
          </div>
        </div>
      )}

      {err && !loading && (
        <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" role="alert">
          {err}
        </div>
      )}

      {data && !loading && <PublicoCard data={data} />}
    </div>
  );
}

function PublicoCard({ data }) {
  const initials = (data.nombre_completo || "CL")
    .split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const badges = Object.entries(data.verificado || {}).filter(([, v]) => v).map(([k]) => k);
  return (
    <article className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="perfil-publico-card">
      <div className="p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          {data.foto_url ? (
            <img src={data.foto_url} alt={data.nombre_completo} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border border-slate-100" />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-brand-blue to-brand-navy text-white text-3xl font-bold flex items-center justify-center">
              {initials}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="perfil-publico-nombre">
              {data.nombre_completo}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              <span className="capitalize font-semibold text-slate-700">{data.tipo_usuario}</span>
            </p>
            {data.ubicacion_publica && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid="perfil-publico-ubic">
                <MapPin size={14} /> {data.ubicacion_publica}
              </p>
            )}
            {badges.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="perfil-publico-badges">
                {badges.map((b) => (
                  <span key={b} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 capitalize">
                    <BadgeCheck size={12} /> {b}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {data.descripcion_corta && (
          <section className="mt-6 pt-6 border-t border-slate-100">
            <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Sobre mí</h2>
            <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="perfil-publico-descripcion">
              {data.descripcion_corta}
            </p>
          </section>
        )}
      </div>
    </article>
  );
}
